/**
 * Engines — the mid-orbit composites in the simplified model.
 *
 * These are the 3 latent elements that feed Brand Equity through another
 * driver and aren't themselves one of the 7 Boosters: Behavioural Loyalty,
 * Attitudinal Loyalty, and Quality Value Tradeoff (PQV).
 *
 * Brand Attachment and Alignment Depth ARE both engines AND boosters — they
 * appear in the inner orbit (BOOSTERS) and also act as feeders of other LEs.
 * The orbital topology config handles those dual roles.
 *
 * Feeders + weights mirror `brand-equity-api/src/brand-equity/latent-elements.config.ts`.
 */

export interface EngineFeeder {
  /** Canonical name. */
  canonical: string;
  /** Whether the feeder is a latent element or a question category. */
  type: 'LE' | 'QC';
  /** Weight from the API model — used for line widths in the orbital. */
  weight: number;
  /** Sign of contribution — -1 for inputs that drag equity down (Price Deals). */
  sign: 1 | -1;
}

export interface EngineConfig {
  /** Canonical name (same as LE name). */
  canonical: string;
  /** What this engine captures — used as the drawer "meaning". */
  meaning: string;
  /** Which driver(s) this engine feeds, and at what weight. */
  feedsInto: ReadonlyArray<{ canonical: string; weight: number }>;
  /** What feeds this engine — for the expandable feeder chip on the orbital. */
  feeders: readonly EngineFeeder[];
}

export const ENGINES: readonly EngineConfig[] = Object.freeze([
  {
    canonical: 'Behavioural Loyalty',
    meaning:
      'Likelihood of continuing to choose the brand: genuine future commitment.',
    feedsInto: [{ canonical: 'Brand Loyalty', weight: 0.35 }],
    feeders: [
      { canonical: 'Alignment Depth', type: 'LE', weight: 0.3, sign: 1 },
      {
        canonical: 'Willingness to Pay a Premium',
        type: 'LE',
        weight: 0.45,
        sign: 1,
      },
      { canonical: 'Repurchase Intention', type: 'QC', weight: 0.25, sign: 1 },
    ],
  },
  {
    canonical: 'Attitudinal Loyalty',
    meaning:
      'Loyalty beyond buying: recommending, trusting, identifying with the brand.',
    feedsInto: [{ canonical: 'Brand Loyalty', weight: 0.25 }],
    feeders: [
      { canonical: 'Standardised NPS', type: 'QC', weight: 0.35, sign: 1 },
      { canonical: 'User Congruence', type: 'QC', weight: 0.25, sign: 1 },
      { canonical: 'Brand Identification', type: 'QC', weight: 0.2, sign: 1 },
      { canonical: 'Brand Trust', type: 'QC', weight: 0.2, sign: 1 },
    ],
  },
  {
    canonical: 'Quality Value Tradeoff (PQV)',
    meaning:
      'The balance between product quality and cost: genuine value-for-money.',
    feedsInto: [
      { canonical: 'Willingness to Pay a Premium', weight: 0.35 },
    ],
    feeders: [
      {
        canonical: 'Perceived Value for the Cost',
        type: 'QC',
        weight: 0.7,
        sign: 1,
      },
      { canonical: 'Product Richness', type: 'QC', weight: 0.3, sign: 1 },
    ],
  },
]);

/** Lookup by canonical name. */
export function getEngineConfig(
  canonical: string,
): EngineConfig | undefined {
  return ENGINES.find((e) => e.canonical === canonical);
}
